// src/components/suppliers/SupplierList.js
import React, { useState } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  Chip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Business,
  Visibility,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";

const SupplierList = ({ onView, onEdit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: suppliers, loading } = useCollection("suppliers");
  const [localSuppliers, setLocalSuppliers] = useState(null);
  const { remove } = useAuditFirestore("suppliers");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  React.useEffect(() => {
    if (suppliers) {
      setLocalSuppliers(suppliers);
    }
  }, [suppliers]);

  React.useEffect(() => {
    setPage(0);
  }, [search]);

  const filtered = localSuppliers?.filter(
    (s) =>
      s.isActive !== false &&
      (search === "" ||
        (s.companyName || "").toLowerCase().includes(search.toLowerCase()) ||
        s.email?.toLowerCase().includes(search.toLowerCase()) ||
        s.phone?.toLowerCase().includes(search.toLowerCase()) ||
        s.taxId?.toLowerCase().includes(search.toLowerCase()))
  );

  const paginatedSuppliers = filtered?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDisplayName = (supplier) => {
    return supplier.companyName || "Sin nombre";
  };

  const getInitials = (supplier) => {
    return (supplier.companyName || "??").substring(0, 2).toUpperCase();
  };

  if (loading || !localSuppliers) return <LoadingSpinner />;

  return (
    <Box>
      {/* Barra de búsqueda */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "rgba(0, 95, 101, 0.12)",
          background: theme.palette.background.paper,
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1.5}
          flexWrap="wrap"
        >
          <TextField
            placeholder="Buscar por nombre, email o CIF"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 280, md: 360 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: 44,
                display: "flex",
                alignItems: "center",
                "& fieldset": {
                  borderColor: theme.palette.divider,
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    ✖
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={`Total: ${filtered?.length || 0}`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                background: theme.palette.background.paper,
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />

            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => {
                  if (suppliers) setLocalSuppliers(suppliers);
                }}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {filtered?.length === 0 ? (
        <EmptyState
          title={search ? "Sin resultados" : "Sin proveedores"}
          description={
            search ? "Prueba con otros términos" : "Añade tu primer proveedor"
          }
          actionLabel="Nuevo Proveedor"
          onAction={() => onEdit(null)}
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      width: { xs: 50, sm: 60 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    {isMobile ? "" : "Avatar"}
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                  >
                    Proveedor
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Email
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        Teléfono
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, color: theme.palette.primary.main, fontSize: "0.85rem" }}
                      >
                        CIF/NIF
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      width: { xs: 100, sm: 130 },
                      color: theme.palette.primary.main,
                      fontSize: { xs: "0.75rem", sm: "0.85rem" },
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedSuppliers?.map((supplier) => (
                  <TableRow
                    key={supplier.id}
                    hover
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0, 95, 101, 0.02)",
                      },
                    }}
                  >
                    <TableCell onClick={() => onView(supplier)}>
                      <Avatar
                        sx={{
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          fontSize: { xs: "0.8rem", sm: "1rem" },
                          fontWeight: 500,
                        }}
                      >
                        <Business fontSize="small" />
                      </Avatar>
                    </TableCell>
                    <TableCell onClick={() => onView(supplier)}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                        {getDisplayName(supplier)}
                      </Typography>
                      {isMobile && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          {supplier.email || "—"}
                        </Typography>
                      )}
                      {supplier.contactName && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
                          Contacto: {supplier.contactName}
                        </Typography>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <>
                        <TableCell onClick={() => onView(supplier)}>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {supplier.email || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={() => onView(supplier)}>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {supplier.phone || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell onClick={() => onView(supplier)}>
                          <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                            {supplier.taxId || "—"}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        <Tooltip title="Ver detalle">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onView(supplier);
                            }}
                            sx={{ color: theme.palette.info.main }}
                          >
                            <Visibility sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(supplier);
                            }}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(supplier);
                            }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filtered?.length || 0}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[7, 10, 25, 50]}
            labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: theme.palette.divider,
              "& .MuiTablePagination-toolbar": {
                minHeight: 48,
                px: { xs: 1, sm: 2 },
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: { xs: "0.7rem", sm: "0.85rem" },
              },
            }}
          />
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          setLocalSuppliers((prev) =>
            prev.filter((s) => s.id !== deleteTarget.id),
          );
          const deletedSupplier = deleteTarget;
          setDeleteTarget(null);

          try {
            await remove(deletedSupplier.id);
          } catch (error) {
            console.error("Error deleting:", error);
            setLocalSuppliers((prev) => [...prev, deletedSupplier]);
          }
        }}
        title="Eliminar Proveedor"
        message={`¿Eliminar a ${getDisplayName(deleteTarget || {})}?`}
        type="delete"
      />
    </Box>
  );
};

export default SupplierList;